import type { ScanGroupOptimized, ScanRootOptimized, ScanTaskOptimized, ScanVideo } from '@kaiizen/kai-scan-template-system';
import { isOtherTask } from '@kaiizen/kai-scan-template-system';

import {
  getGroupTitle,
  getPartLocalVideoEntries,
  getPartVideoEntries,
  getTaskTitle,
} from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatDate } from '@/Client/Presenters';

import {
  type BuildFocusedPhotoStripEntriesParams,
  type FocusedPhotoStripEditorMode,
  getFocusedPhotoStripEditorConfig,
  getFocusedPhotoWalkLabel,
} from './focusedPhotoStripLib';
import type { FocusedPhotoStripEntry } from './focusedStrip/focusedStripTypes';
import {
  GROUP_PHOTO_CHIP_LABEL,
  NON_STANDARD_TASK_CHIP_LABEL_PREFIX,
  TASK_PHOTO_CHIP_LABEL_FALLBACK,
} from './focusedStrip/focusedStripTypes';

export type FocusedMediaStripVideoScope = 'task' | 'group' | 'scope';

export type FocusedMediaStripVideoEntry = {
  videoId: string;
  scope: FocusedMediaStripVideoScope;
  addedAt?: string | number;
  addedByUserId?: string;
  taskLabel?: string;
  groupLabel?: string;
  dateLabel?: string;
  walkLabel?: string;
  groupId?: string;
  taskId?: string;
};

export const toFocusedMediaStripVideoEntry = (
  video: ScanVideo,
  scope: FocusedMediaStripVideoScope,
  context: { groupId?: string; groupLabel?: string; taskId?: string; taskLabel?: string } = {},
): FocusedMediaStripVideoEntry => ({
  videoId: video.videoId,
  scope,
  addedAt: video.addedAt,
  addedByUserId: video.addedByUserId,
  dateLabel: video.addedAt ? formatDate(video.addedAt) : undefined,
  walkLabel: getFocusedPhotoWalkLabel({ photoId: video.videoId, tags: video.tags }),
  ...context,
});

const sortFocusedMediaStripVideoEntriesOldestFirst = (
  entries: readonly FocusedMediaStripVideoEntry[],
): FocusedMediaStripVideoEntry[] =>
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => {
      const leftTime = typeof left.entry.addedAt === 'number'
        ? left.entry.addedAt
        : left.entry.addedAt
        ? Date.parse(left.entry.addedAt)
        : Number.NaN;
      const rightTime = typeof right.entry.addedAt === 'number'
        ? right.entry.addedAt
        : right.entry.addedAt
        ? Date.parse(right.entry.addedAt)
        : Number.NaN;
      const leftHasTime = Number.isFinite(leftTime);
      const rightHasTime = Number.isFinite(rightTime);

      if (leftHasTime && rightHasTime && leftTime !== rightTime) return leftTime - rightTime;
      if (leftHasTime !== rightHasTime) return leftHasTime ? -1 : 1;
      return left.index - right.index;
    })
    .map(({ entry }) => entry);

const SCOPE_RANK: Record<FocusedMediaStripVideoScope, number> = {
  scope: 1,
  group: 2,
  task: 3,
};

const appendUniqueVideoEntries = (
  target: FocusedMediaStripVideoEntry[],
  entryIndexByVideoId: Map<string, number>,
  videos: readonly ScanVideo[],
  scope: FocusedMediaStripVideoScope,
  context: { groupId?: string; groupLabel?: string; taskId?: string; taskLabel?: string },
  options?: {
    preferExistingScopes?: readonly FocusedMediaStripVideoScope[];
    /** Group editor: group is appended before tasks — keep the first scope for a shared videoId. */
    keepFirstScopeOnDuplicate?: boolean;
  },
) => {
  for (const video of videos) {
    if (!video.videoId) continue;
    const existingIndex = entryIndexByVideoId.get(video.videoId);
    if (existingIndex != null) {
      const existingScope = target[existingIndex]?.scope;
      if (existingScope && options?.preferExistingScopes?.includes(existingScope)) {
        continue;
      }
      if (
        !options?.keepFirstScopeOnDuplicate
        && existingScope
        && SCOPE_RANK[scope] > SCOPE_RANK[existingScope]
      ) {
        target[existingIndex] = toFocusedMediaStripVideoEntry(video, scope, context);
      }
      continue;
    }
    entryIndexByVideoId.set(video.videoId, target.length);
    target.push(toFocusedMediaStripVideoEntry(video, scope, context));
  }
};

const appendGroupVideoEntries = (
  target: FocusedMediaStripVideoEntry[],
  entryIndexByVideoId: Map<string, number>,
  group: ScanGroupOptimized,
  options?: {
    preferExistingScopes?: readonly FocusedMediaStripVideoScope[];
    keepFirstScopeOnDuplicate?: boolean;
    localVideosOnly?: boolean;
  },
) => {
  const groupLabel = getGroupTitle(group, GROUP_PHOTO_CHIP_LABEL);
  appendUniqueVideoEntries(
    target,
    entryIndexByVideoId,
    options?.localVideosOnly ? getPartLocalVideoEntries(group) : getPartVideoEntries(group),
    'group',
    { groupId: group.id, groupLabel },
    options,
  );
};

const appendTaskVideoEntries = (
  target: FocusedMediaStripVideoEntry[],
  entryIndexByVideoId: Map<string, number>,
  group: ScanGroupOptimized,
  task: ScanTaskOptimized,
  options?: {
    preferExistingScopes?: readonly FocusedMediaStripVideoScope[];
    keepFirstScopeOnDuplicate?: boolean;
    localVideosOnly?: boolean;
  },
) => {
  const taskLabel = task.template && isOtherTask(task) && task.customLabel
    ? `${NON_STANDARD_TASK_CHIP_LABEL_PREFIX}${task.customLabel}`
    : getTaskTitle(task, TASK_PHOTO_CHIP_LABEL_FALLBACK);
  appendUniqueVideoEntries(
    target,
    entryIndexByVideoId,
    options?.localVideosOnly ? getPartLocalVideoEntries(task) : getPartVideoEntries(task),
    'task',
    { groupId: group.id, taskId: task.id, taskLabel },
    options,
  );
};

const GROUP_TASK_STRIP_VIDEO_OPTIONS = {
  localVideosOnly: true,
  keepFirstScopeOnDuplicate: true,
} as const;

export type BuildFocusedMediaStripVideoEntriesParams =
  | { mode: 'task'; group: ScanGroupOptimized; task: ScanTaskOptimized }
  | { mode: 'group'; group: ScanGroupOptimized }
  | { mode: 'scope'; scanRoot: ScanRootOptimized; groups: readonly ScanGroupOptimized[] };

/** Builds ordered, deduped strip video entries for task, group, or all-scope edit routes. */
export const buildFocusedMediaStripVideoEntries = (
  params: BuildFocusedMediaStripVideoEntriesParams,
): FocusedMediaStripVideoEntry[] => {
  const entries: FocusedMediaStripVideoEntry[] = [];
  const entryIndexByVideoId = new Map<string, number>();

  if (params.mode === 'task') {
    appendTaskVideoEntries(entries, entryIndexByVideoId, params.group, params.task, GROUP_TASK_STRIP_VIDEO_OPTIONS);
    return sortFocusedMediaStripVideoEntriesOldestFirst(entries);
  }

  if (params.mode === 'group') {
    appendGroupVideoEntries(entries, entryIndexByVideoId, params.group, GROUP_TASK_STRIP_VIDEO_OPTIONS);
    for (const task of params.group.tasks ?? []) {
      appendTaskVideoEntries(entries, entryIndexByVideoId, params.group, task, GROUP_TASK_STRIP_VIDEO_OPTIONS);
    }
    return sortFocusedMediaStripVideoEntriesOldestFirst(entries);
  }

  const preferScopeEntry = { preferExistingScopes: ['scope'] as const };
  appendUniqueVideoEntries(
    entries,
    entryIndexByVideoId,
    getPartVideoEntries(params.scanRoot),
    'scope',
    { groupLabel: 'Scope' },
    preferScopeEntry,
  );
  for (const group of params.groups) {
    appendGroupVideoEntries(entries, entryIndexByVideoId, group, preferScopeEntry);
    for (const task of group.tasks ?? []) {
      appendTaskVideoEntries(entries, entryIndexByVideoId, group, task, preferScopeEntry);
    }
  }
  return sortFocusedMediaStripVideoEntriesOldestFirst(entries);
};

export type FocusedMediaStripEditorMode = BuildFocusedPhotoStripEntriesParams['mode'];

export const getFocusedMediaStripEditorConfig = ({
  group,
  groups,
  scanRoot,
  task,
  taskId,
}: {
  group?: ScanGroupOptimized;
  groups: readonly ScanGroupOptimized[];
  scanRoot: ScanRootOptimized;
  task?: ScanTaskOptimized;
  taskId?: string;
}): {
  photoEntries: FocusedPhotoStripEntry[];
  videoEntries: FocusedMediaStripVideoEntry[];
  title: string;
  emptyLabel: string;
  actionLabel?: string;
  galleryScope: { groupId?: string; taskId?: string };
  mode: FocusedPhotoStripEditorMode;
} => {
  const photoConfig = getFocusedPhotoStripEditorConfig({ group, groups, task, taskId });

  if (taskId && group && task) {
    return {
      ...photoConfig,
      title: 'Task Media',
      emptyLabel: 'No media yet for this task',
      videoEntries: buildFocusedMediaStripVideoEntries({ mode: 'task', group, task }),
      photoEntries: photoConfig.entries,
    };
  }

  if (group) {
    return {
      ...photoConfig,
      title: 'Group and Task Media',
      emptyLabel: 'No media yet for this group',
      videoEntries: buildFocusedMediaStripVideoEntries({ mode: 'group', group }),
      photoEntries: photoConfig.entries,
    };
  }

  return {
    ...photoConfig,
    title: 'Group and Task Media',
    emptyLabel: 'No media yet for this scope',
    videoEntries: buildFocusedMediaStripVideoEntries({ mode: 'scope', scanRoot, groups }),
    photoEntries: photoConfig.entries,
  };
};

export type { FocusedPhotoStripEditorMode };
